import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { Reveal, EASE } from "@/components/site/Reveal";
import { PageHero } from "@/components/site/PageHero";
import { BookingBand } from "@/components/site/BookingBand";
import { Sparkle } from "@/components/site/Sparkle";
import { IMG, GALLERY_ITEMS } from "@/data/site";

const FILTERS = ["All", "Hair", "Colour", "Skin & Laser"];

export default function Gallery() {
  const [filter, setFilter] = useState("All");
  const [active, setActive] = useState(null);
  const items = GALLERY_ITEMS.filter((g) => filter === "All" || g.category === filter);

  return (
    <>
      <PageHero
        testId="gallery-hero"
        eyebrow="Gallery"
        titleLines={["Results,", "Not Promises"]}
        quote="A closer look at the work we're proud of."
        image={IMG.hair3}
        imageAlt="Finished colour work catching warm light at the S & Z studio"
      />

      <section data-testid="gallery-grid-section" className="bg-ivory py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-16">
          <Reveal className="flex flex-wrap items-center gap-4 mb-14">
            {FILTERS.map((f) => (
              <button
                key={f}
                data-testid={`gallery-filter-${f.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setFilter(f)}
                className={`font-sans uppercase tracking-[0.18em] text-[11px] font-medium px-6 py-2.5 border transition-all duration-300 ${
                  filter === f
                    ? "border-gold bg-gold text-espresso"
                    : "border-stone text-deep/60 hover:border-gold hover:text-gold"
                }`}
              >
                {f}
              </button>
            ))}
          </Reveal>

          <motion.div layout className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-10">
            <AnimatePresence mode="popLayout">
              {items.map((g, i) => (
                <motion.button
                  layout
                  key={g.title}
                  data-testid={`gallery-item-${i}`}
                  onClick={() => setActive(g)}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.97 }}
                  transition={{ duration: 0.6, ease: EASE, delay: i * 0.05 }}
                  className={`group text-left ${i % 3 === 1 ? "lg:mt-14" : ""}`}
                >
                  <div className="overflow-hidden img-frame border border-stone group-hover:border-gold group-hover:-translate-y-1.5 transition-all duration-500">
                    <img
                      src={g.src}
                      alt={g.title}
                      loading="lazy"
                      className="w-full h-[340px] sm:h-[400px] object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                  </div>
                  <div className="flex items-center justify-between mt-4">
                    <p className="font-sans uppercase tracking-[0.2em] text-[11px] text-deep/60">
                      {g.title}
                    </p>
                    <p className="font-sans uppercase tracking-[0.2em] text-[10px] text-gold flex items-center gap-2">
                      <Sparkle size={8} /> {g.category}
                    </p>
                  </div>
                </motion.button>
              ))}
            </AnimatePresence>
          </motion.div>
        </div>
      </section>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="gallery-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-[80] bg-espresso/95 flex items-center justify-center p-6 sm:p-14"
            onClick={() => setActive(null)}
          >
            <button
              data-testid="gallery-lightbox-close"
              onClick={() => setActive(null)}
              aria-label="Close image"
              className="absolute top-6 right-6 text-ivory/70 hover:text-gold transition-colors p-2"
            >
              <X size={26} strokeWidth={1} />
            </button>
            <motion.figure
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.45, ease: EASE }}
              className="max-w-3xl w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={active.src}
                alt={active.title}
                className="w-full max-h-[75vh] object-cover border border-gold/40"
              />
              <figcaption className="flex items-center justify-between mt-5">
                <span className="font-display text-2xl text-ivory">{active.title}</span>
                <span className="font-sans uppercase tracking-[0.2em] text-[10px] text-champagne">
                  {active.category}
                </span>
              </figcaption>
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>

      <BookingBand />
    </>
  );
}
